// ============================================================
// Attendance module manifest — 2026-07-27.
//
// Owns QR clock-in/out (Time In, Lunch Out, Lunch In, Time Out),
// the derived day/hours records, the missing-record evidence +
// approval workflow, and the working-hours policy.
//
// Depends on hr — every surface here resolves the signed-in user to
// an employee record (fetchMyEmployee), and the manager views list
// employees. Payroll consumes the frozen AttendanceSummary off the
// payslip doc rather than importing this module, so a tenant can run
// payroll with attendance switched off.
//
// Routes:
//   my-attendance     — every staff member. Scan + own month.
//   attendance        — managers. Approval queue / today / records / settings.
//   attendance-kiosk  — managers. The rotating QR screen at the door.
// ============================================================

export default {
  id:          'attendance',
  name:        'Attendance',
  version:     '1.1.0',
  description: 'QR / typed-code clock-in/out plus optional phone clock-in (passkey + location check, HR-approved phones), lunch breaks, 8-hour daily minimum, evidence-backed exception approvals, and a monthly attendance snapshot on the payslip.',

  load: () => import('./index.js'),

  routes: [
    {
      id:      'my-attendance',
      label:   'My Attendance',
      icon:    '⏱',
      // 'account' = the self-service group, alongside My Payslips and
      // My Profile. This is where a staff member looks for their own things.
      section: 'account',
      sidebarPosition: 20,
      visible: (role, hasPerm) => hasPerm('attendance.punch'),
      mount:   'mountMyAttendance',
      unmount: 'unmountMyAttendance'
    },
    {
      id:      'attendance',
      label:   'Attendance',
      icon:    '🕘',
      section: 'people',
      sidebarPosition: 40,
      // The manager surface. Gated on read.all so staff never see it —
      // their own view lives at my-attendance.
      visible: (role, hasPerm) => hasPerm('attendance.read.all'),
      mount:   'mountAttendanceAdmin',
      unmount: 'unmountAttendanceAdmin'
    },
    {
      id:      'attendance-kiosk',
      label:   'Attendance Kiosk',
      icon:    '📺',
      section: 'people',
      sidebarPosition: 50,
      visible: (role, hasPerm) => hasPerm('attendance.kiosk'),
      mount:   'mountAttendanceKiosk',
      unmount: 'unmountAttendanceKiosk'
    }
  ],

  schemas: [
    'attendancepunches',      // append-only raw punch log (Cloud Function writes only)
    'attendancedays',         // derived per (employee, date) rollup
    'attendanceexceptions',   // MC / missing-scan claims + approval state
    'attendancesites',        // clock-in points (kiosk screen or printed poster)
    'attendancepolicy',       // shift times + the 8-hour rule + lunch config
    'attendancesecrets',      // per-site HMAC keys — Rules deny ALL client access
    'attendancethrottle',     // per-uid manual-code guess throttle + phone clock-in mint throttle / refused-attempt
                              //   log (doc id 'dev__<uid>') — Rules deny ALL client access
    // 2026-10-19 — Phone clock-in (device method). DESIGN_LOG 2026-10-19 synthesis §4.
    'attendancedevices',      // enrolled phones: public status only (HR all; owner self-read). Client write NEVER
    'attendancedevicenotes',  // HR-only internal notes on reject / revoke (device doc is owner-readable) — client write NEVER
    'attendancedevicestate',  // passkey public key + counter + install-id hash + baselines — Rules deny ALL client access
    'attendancechallenges',   // single-use WebAuthn challenges (SHA-256 only, TTL on ExpireAt) — Rules deny ALL client access
    'attendancepunchreviews', // flagged phone clock-ins + HR decision — attendance.devices.manage ONLY (no supervisor, no self)
    'attendancepunchgeo'      // raw GPS samples per punch, 90-day TTL on ExpireAt — attendance.devices.manage ONLY
  ],

  permissions: [
    'attendance.punch',         // clock in/out for yourself + see your own month
    'attendance.read.all',      // see everyone's attendance (manager)
    'attendance.manage',        // punch on behalf of others, rebuild day records
    'attendance.approve',       // approve / reject exception requests
    'attendance.edit',          // correct a staff member's clock times for a past day (director/admin/hr ONLY —
                                //   deliberately narrower than attendance.manage, which lets a supervisor record
                                //   a punch happening NOW but not rewrite history)
    'attendance.kiosk',         // open the QR kiosk screen
    'attendance.policy.write',  // edit shift times, hours rule, sites
    // D5 (2026-09-22) — read the MEDICAL EVIDENCE attached to an exception
    // (the sibling collection attendance-exception-evidence). Director's
    // ruling: HR and Director only. Deliberately NOT implied by
    // attendance.read.all or attendance.manage — production-supervisor and
    // store-manager hold both of those, so neither can be the gate. `hr`
    // picks this up automatically through its `attendance.*` wildcard;
    // supervisors hold explicit literals and so do not.
    'attendance.evidence.read',
    // 2026-10-19 — Phone clock-in: approve / reject / revoke staff phones,
    // read flagged clock-ins + raw GPS, accept / void flagged clock-ins.
    // Deliberately NOT held by production-supervisor / store-manager (they
    // carry explicit literals, so they do not pick it up; hr gets it through
    // 'attendance.*', director via '*', admin via the fast-path):
    //   - approving a phone issues a credential that lets someone
    //     self-certify presence — a colluding supervisor could approve a
    //     buddy's phone (supervisors can already proxy-punch, but only
    //     openly as Method 'manual');
    //   - raw coordinates and which heuristic fired are location personal
    //     data (PDPA) — supervisors see only the punch and "under review".
    // ⚠ Existing tenants: role docs are MATERIALIZED, so hr gets this only
    //   after scripts/grant-perm.mjs adds it (see the deploy steps).
    'attendance.devices.manage'
  ],

  dependencies: ['hr'],

  fitsIndustries: ['manufacturing', 'trading', 'services', 'retail'],

  functions: [
    'punchAttendance',
    'getAttendanceKioskTokens',
    'reviewAttendanceException',
    'recomputeAttendanceDays',
    'adminSetAttendanceTimes',
    'resetAttendanceScans',
    // 2026-10-19 — Phone clock-in.
    'attendanceDeviceEnrol',
    'punchAttendanceDevice',
    'reviewAttendanceDevice',
    'reviewFlaggedPunch'
  ],

  dashboardWidgets: [
    {
      id:           'attendance-clock-cta',
      label:        'Clock In / Out',
      section:      'kpi',
      // Order 4 puts it immediately BEFORE the Apply Leave tile (order 5)
      // — clocking in happens twice a day, applying for leave a few times
      // a year, so it earns the first slot on a staff dashboard.
      order:        4,
      defaultRoles: ['staff', 'shop-floor', 'production-supervisor', 'sales']
    },
    {
      id:           'attendance-pending-kpi',
      label:        'Attendance requests',
      section:      'hr-block',
      order:        15,
      defaultRoles: ['director', 'hr', 'admin']
    },
    {
      id:           'attendance-in-today-kpi',
      label:        'On site now',
      section:      'hr-block',
      order:        16,
      defaultRoles: ['director', 'hr', 'admin']
    }
  ]
};
